/* eslint 'sort-keys-fix/sort-keys-fix': ['error'] */
import type { DisplayCategories } from '@frameio/vapor';
import { theme } from '@frameio/vapor';
import {
  IconFiletypeAudioFill,
  IconFiletypeBlankFill,
  IconFiletypeCompressedFill,
  IconFiletypeDrawingFill,
  IconFiletypeImageFill,
  IconFiletypeInteractiveFill,
  IconFiletypeModelFill,
  IconFiletypeMotionFill,
  IconFiletypePdfFill,
  IconFiletypePresentationFill,
  IconFiletypeSpreadsheetFill,
  IconFiletypeTextFill,
  IconFiletypeVectorFill,
  IconFiletypeVideoFill,
  IconFolderClosedFill,
  IconLayout,
} from '@frameio/vapor-icons';

const graphqlMedia = {
  AudioMedia: 'AudioMedia',
  DocumentMedia: 'DocumentMedia',
  Folder: 'Folder',
  ImageMedia: 'ImageMedia',
  InteractiveLiveMedia: 'InteractiveLiveMedia',
  InteractiveMedia: 'InteractiveMedia',
  ModelMedia: 'ModelMedia',
  UnsupportedMedia: 'UnsupportedMedia',
  VideoMedia: 'VideoMedia',
};

export type GraphqlMedia = keyof typeof graphqlMedia;

const TEXT = {
  AUDIO: 'Audio',
  BATCH: 'Batch',
  DRAWING: 'Drawing',
  FOLDER: 'Folder',
  IMAGE: 'Image',
  INTERACTIVE_LIVE_MEDIA: 'Web URL',
  MODEL: '3D Model',
  MOTION: 'Motion',
  PDF_DOCUMENT: 'PDF Document',
  PRESENTATION: 'Presentation',
  PROJECT: 'Project',
  TEXT_DOCUMENT: 'Text Document',
  UNKNOWN: 'Unknown',
  VECTOR: 'Vector',
  VIDEO: 'Video',
  XLS_DOCUMENT: 'XLS Document',
  ZIP_ARCHIVE: 'Zip archive',
};
type IconType = typeof IconFiletypeVideoFill; // placeholder icon to extract type
type MimeTypeHelper = {
  /** Supports both prefixes and full types. `type/*` or `type/xyz` */
  mimeTypes?: string[];
  /** Vapor specific Icon component */
  Icon: IconType;
  backgroundColor: string;
  color: string;
  /** Fallback copy used */
  copy: string;
  /** optional type as defined by graphql */
  type?: (typeof graphqlMedia)[keyof typeof graphqlMedia];
  displayCategory: DisplayCategories;
};

const NOT_MATCHED_TYPE: MimeTypeHelper = {
  Icon: IconFiletypeBlankFill,
  backgroundColor: theme.colors.components.filetype.blank.background,
  color: theme.colors.components.filetype.blank.text,
  copy: TEXT.UNKNOWN,
  displayCategory: 'unsupported',
  mimeTypes: [],
  type: graphqlMedia.UnsupportedMedia,
};

/**
 * Eventually, this categorization of mimeTypes will live on the back-end,
 * but for now we'll generalize it here.
 *
 * Do not export this directly. It should be used through the
 * helper utils below.
 */
const MATCHING_MIMES: MimeTypeHelper[] = [
  /** Document/PDF */
  {
    Icon: IconFiletypePdfFill,
    backgroundColor: theme.colors.components.filetype?.pdf.background,
    color: theme.colors.components.filetype?.pdf.text,
    copy: TEXT.PDF_DOCUMENT,
    displayCategory: 'pdf',
    mimeTypes: ['application/pdf'],
    type: graphqlMedia.DocumentMedia,
  },
  /** spreadsheet/xls */
  {
    Icon: IconFiletypeSpreadsheetFill,
    backgroundColor: theme.colors.components.filetype?.spreadsheet.background,
    color: theme.colors.components.filetype?.spreadsheet.text,
    copy: TEXT.XLS_DOCUMENT,
    displayCategory: 'spreadsheet',
    mimeTypes: [
      'application/xls',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    type: graphqlMedia.DocumentMedia,
  },
  /** Compressed/Zip */
  /** This type has no mimeType as it's to differentiate from the zip archive
   * type that's not marked as interactive */
  {
    Icon: IconFiletypeInteractiveFill,
    backgroundColor: theme.colors.components.filetype?.interactive.background,
    color: theme.colors.components.filetype?.interactive.text,
    copy: TEXT.ZIP_ARCHIVE,
    displayCategory: 'interactive',
    type: graphqlMedia.InteractiveMedia,
  },
  /** Interactive Live */
  {
    Icon: IconFiletypeInteractiveFill,
    backgroundColor: theme.colors.components.filetype?.interactive.background,
    color: theme.colors.components.filetype?.interactive.text,
    copy: TEXT.INTERACTIVE_LIVE_MEDIA,
    displayCategory: 'interactive',
    type: graphqlMedia.InteractiveLiveMedia,
  },
  {
    Icon: IconFiletypeCompressedFill,
    backgroundColor: theme.colors.components.filetype?.compressed.background,
    color: theme.colors.components.filetype?.compressed.text,
    copy: TEXT.ZIP_ARCHIVE,
    displayCategory: 'compressed',
    mimeTypes: ['application/zip'],
  },
  /** Audio */
  {
    Icon: IconFiletypeAudioFill,
    backgroundColor: theme.colors.components.filetype?.audio.background,
    color: theme.colors.components.filetype?.audio.text,
    copy: TEXT.AUDIO,
    displayCategory: 'audio',
    mimeTypes: ['audio/*'],
    type: graphqlMedia.AudioMedia,
  },
  /** Image */
  {
    Icon: IconFiletypeImageFill,
    backgroundColor: theme.colors.components.filetype?.image.background,
    color: theme.colors.components.filetype?.image.text,
    copy: TEXT.IMAGE,
    displayCategory: 'image',
    mimeTypes: ['image/*'],
    type: graphqlMedia.ImageMedia,
  },
  /** Text / Word Docs */
  {
    Icon: IconFiletypeTextFill,
    backgroundColor: theme.colors.components.filetype?.text.background,
    color: theme.colors.components.filetype?.text.text,
    copy: TEXT.TEXT_DOCUMENT,
    displayCategory: 'text',
    mimeTypes: [
      'text/*',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.oasis.opendocument.text',
    ],
    type: graphqlMedia.DocumentMedia,
  },
  /** Video */
  {
    Icon: IconFiletypeVideoFill,
    backgroundColor: theme.colors.components.filetype?.video.background,
    color: theme.colors.components.filetype?.video.text,
    copy: TEXT.VIDEO,
    displayCategory: 'video',
    mimeTypes: ['video/*'],
    type: graphqlMedia.VideoMedia,
  },
  /** After Effects */
  {
    Icon: IconFiletypeMotionFill,
    backgroundColor: theme.colors.components.filetype?.motion.background,
    color: theme.colors.components.filetype?.motion.text,
    copy: TEXT.MOTION,
    displayCategory: 'motion',
    mimeTypes: [
      'application/vnd.adobe.aftereffects.project',
      'application/vnd.adobe.aftereffects.template',
      'application/vnd.audiograph',
    ],
  },
  /** Drawing Photoshop / editing rasterized files */
  {
    Icon: IconFiletypeDrawingFill,
    backgroundColor: theme.colors.components.filetype?.drawing.background,
    color: theme.colors.components.filetype?.drawing.text,
    copy: TEXT.DRAWING,
    displayCategory: 'drawing',
    mimeTypes: [
      'drawing/*',
      'application/adobe.photoshop',
      'application/photoshop',
      'application/postscript',
      'image/vnd.adobe.photoshop',
    ],
    type: graphqlMedia.ImageMedia,
  },
  /** Vector based files */
  {
    Icon: IconFiletypeVectorFill,
    backgroundColor: theme.colors.components.filetype?.vector.background,
    color: theme.colors.components.filetype?.vector.text,
    copy: TEXT.VECTOR,
    displayCategory: 'vector',
    mimeTypes: [
      'vector/*',
      'application/adobe.illustrator',
      'application/illustrator',
      'image/svg+xml',
    ],
    type: graphqlMedia.ImageMedia,
  },
  /** Presentation files */
  {
    Icon: IconFiletypePresentationFill,
    backgroundColor: theme.colors.components.filetype?.presentation.background,
    color: theme.colors.components.filetype?.presentation.text,
    copy: TEXT.PRESENTATION,
    displayCategory: 'presentation',
    mimeTypes: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ],
    type: graphqlMedia.DocumentMedia,
  },
  /** 3D Model files */
  {
    Icon: IconFiletypeModelFill,
    backgroundColor: theme.colors.components.filetype.model.background,
    color: theme.colors.components.filetype.model.text,
    copy: TEXT.MODEL,
    displayCategory: 'model',
    mimeTypes: ['model/*'],
    type: graphqlMedia.ModelMedia,
  },
  /**folders (this is a hardcoded type) */
  {
    Icon: IconFolderClosedFill,
    backgroundColor: theme.colors.components.filetype.blank.background,
    color: theme.colors.components.filetype.blank.text,
    copy: TEXT.FOLDER,
    displayCategory: 'folder',
    mimeTypes: ['FOLDER', 'folder'],
    type: graphqlMedia.Folder,
  },
  /** batches (this is a hardcoded type) */
  {
    Icon: IconLayout,
    backgroundColor: theme.colors.components.filetype.blank.background,
    color: theme.colors.components.filetype.blank.text,
    copy: TEXT.BATCH,
    displayCategory: 'batch',
    mimeTypes: ['BATCH', 'batch'],
  },
];

/**
 * getDisplayFileType
 * @description - This function, when given a MIME file type, will return the corresponding file type for display on the front-end. This is useful for ensuring that the correct icon and file type are displayed to the user. For example, a gif file will be displayed as a video file type so it is playable to the user, even though it is classified as an image on the back-end.
 * @param {string} fileType The MIME type of the file.
 * @returns {string} The display category of the file.
 */
export const getDisplayFileType = (fileType: string) => {
  if (fileType === 'image/gif') return 'video';
  return fileType;
};

const findSupportedTypeByGraphQLType = (typename?: string | null) => {
  if (!typename) {
    return NOT_MATCHED_TYPE;
  }

  const match = MATCHING_MIMES.find((type) => type.type === typename);

  return match || NOT_MATCHED_TYPE;
};

const findSupportedTypeByMIMEType = (filetype?: string | null) => {
  if (!filetype) {
    return NOT_MATCHED_TYPE;
  }

  // Handle mimeTypes with prefixes like: `video/*`, `audio/*`, etc:
  const fileTypePrefix = filetype.split('/')[0].concat('/*') || null;

  const [exactMatch, prefixMatch] = MATCHING_MIMES.reduce<
    [MimeTypeHelper | null, MimeTypeHelper | null]
  >(
    ([currentExactMatch, currentPrefixMatch], type) => {
      const prefixMatches =
        fileTypePrefix && type.mimeTypes?.indexOf(fileTypePrefix) === 0;
      const exactMimeTypeMatch =
        filetype && type.mimeTypes && type.mimeTypes.indexOf(filetype) >= 0;

      return [
        exactMimeTypeMatch ? type : currentExactMatch,
        prefixMatches ? type : currentPrefixMatch,
      ];
    },
    [null, null]
  );

  return exactMatch || prefixMatch || NOT_MATCHED_TYPE;
};

export const getGraphqlTypeByFileType = (filetype: string): GraphqlMedia => {
  const { type } = findSupportedTypeByMIMEType(filetype);

  return type as GraphqlMedia;
};

export const getIconByFileType = (
  filetype?: string | null,
  mediaType?: string | null
): {
  Icon: IconType;
  backgroundColor: string;
  color: string;
  displayCategory: DisplayCategories;
} => {
  if (mediaType) return findSupportedTypeByGraphQLType(mediaType);

  const { Icon, backgroundColor, color, displayCategory } =
    findSupportedTypeByMIMEType(filetype);

  return {
    Icon,
    backgroundColor,
    color,
    displayCategory,
  };
};

export const getDisplayCategory = (
  fileType?: string | null,
  mediaType?: string | null
): DisplayCategories => {
  let displayCategory: DisplayCategories | undefined;

  if (mediaType) {
    displayCategory = findSupportedTypeByGraphQLType(mediaType).displayCategory;
  }
  if (!displayCategory || displayCategory === 'unsupported') {
    displayCategory = findSupportedTypeByMIMEType(fileType).displayCategory;
  }
  return displayCategory;
};

export const getCopyByFileType = (
  assetType: string,
  filetype: string
): { copy: string } => {
  if (assetType === 'FOLDER' || assetType === 'Folder') {
    return { copy: TEXT.FOLDER };
  }
  if (assetType === 'PROJECT' || assetType === 'Project') {
    return { copy: TEXT.PROJECT };
  }
  const { copy } = findSupportedTypeByMIMEType(filetype);

  return {
    copy,
  };
};
